import React from "react";
import {
  Linkedin,
  Instagram,
  Facebook,
  Mail,
  MapPin,
  Phone,
  ArrowUp,
} from "lucide-react";

import { heroData } from "../data/mockData";


import { contactData } from "../data/mockData";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);

    if (element) {
      const headerOffset = 90;

      const elementPosition =
        element.getBoundingClientRect().top + window.pageYOffset;

      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const quickLinks = [
    { label: "Home", id: "hero" },
    { label: "Sobre", id: "about" },
    { label: "Áreas de Atuação", id: "areas" },
    { label: "Projetos", id: "projects" },
    { label: "Parceiros", id: "partners" },
    { label: "Contato", id: "contact" },
  ];

  const socialLinks = [
    {
      name: "LinkedIn",
      href: contactData?.social?.linkedin,
      icon: Linkedin,
    },
    {
      name: "Instagram",
      href: contactData?.social?.instagram,
      icon: Instagram,
    },
    {
      name: "Facebook",
      href: contactData?.social?.facebook,
      icon: Facebook,
    },
  ].filter((social) => social.href);

  const hasContactInfo =
    contactData?.address ||
    contactData?.email ||
    contactData?.phone;

  return (
    <footer className="relative bg-[#0b1511] text-white overflow-hidden">
      {/* Elementos decorativos */}
      <div className="absolute top-0 right-0 w-[420px] h-[420px] bg-green-700/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[320px] h-[320px] bg-emerald-700/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative container py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10">
          {/* =====================================================
              BLOCO INSTITUCIONAL
          ===================================================== */}
          <div className="lg:col-span-5">
            <button
              type="button"
              onClick={() => scrollToSection("hero")}
              className="group text-left"
              aria-label="Voltar ao início"
            >
              <img
                src={heroData.logo}
                alt="Instituto Mercosul Amazônia - IMA"
                className="
                  h-[78px]
                  sm:h-[88px]
                  lg:h-[96px]
                  w-auto
                  object-contain
                  mb-6
                  transition-transform
                  duration-300
                  group-hover:scale-[1.02]
                "
              />
            </button>

            <h3 className="text-xl sm:text-2xl font-semibold tracking-tight mb-2">
              Instituto Mercosul Amazônia
            </h3>

            <p className="text-green-400 font-medium mb-5">
              Inovação, desenvolvimento e conexões para a Amazônia
            </p>

            <p className="text-gray-300 leading-relaxed max-w-xl">
              O IMA desenvolve projetos, programas e articulações que conectam
              inovação, empreendedorismo, ciência, tecnologia e desenvolvimento
              territorial na Amazônia.
            </p>

            {/* Redes sociais */}
            {socialLinks.length > 0 && (
              <div className="mt-7">
                <p className="text-xs uppercase tracking-[0.18em] text-gray-500 font-semibold mb-4">
                  Acompanhe o IMA
                </p>

                <div className="flex items-center gap-3">
                  {socialLinks.map((social) => {
                    const Icon = social.icon;

                    return (
                      <a
                        key={social.name}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={social.name}
                        title={social.name}
                        className="
                          w-11 h-11
                          rounded-full
                          border border-white/10
                          bg-white/5
                          hover:bg-green-600
                          hover:border-green-600
                          flex items-center justify-center
                          text-gray-300
                          hover:text-white
                          transition-all
                          duration-200
                        "
                      >
                        <Icon className="w-[18px] h-[18px]" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* =====================================================
              LINKS RÁPIDOS
          ===================================================== */}
          <div className="lg:col-span-3">
            <h4 className="text-base font-semibold mb-6 text-white">
              Links Rápidos
            </h4>

            <nav
              className="flex flex-col items-start gap-3"
              aria-label="Links rápidos do rodapé"
            >
              {quickLinks.map((link) => (
                <button
                  key={link.id}
                  type="button"
                  onClick={() => scrollToSection(link.id)}
                  className="
                    group
                    inline-flex
                    items-center
                    gap-2
                    text-sm
                    text-gray-400
                    hover:text-green-400
                    transition-colors
                  "
                >
                  <span
                    className="
                      w-0
                      group-hover:w-3
                      h-px
                      bg-green-400
                      transition-all
                      duration-200
                    "
                  />

                  {link.label}
                </button>
              ))}
            </nav>
          </div>

          {/* =====================================================
              CONTATO
          ===================================================== */}
          <div className="lg:col-span-4">
            <h4 className="text-base font-semibold mb-6 text-white">
              Contato
            </h4>

            {hasContactInfo ? (
              <div className="space-y-5">
                {/* Endereço */}
                {contactData?.address && (
                  <div className="flex items-start gap-3">
                    <div
                      className="
                        w-9 h-9
                        rounded-lg
                        bg-green-600/10
                        flex items-center justify-center
                        shrink-0
                      "
                    >
                      <MapPin className="w-[17px] h-[17px] text-green-400" />
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                        Endereço
                      </p>

                      <div className="text-sm text-gray-300 leading-relaxed">
                        {contactData.address.street && (
                          <p>{contactData.address.street}</p>
                        )}

                        {contactData.address.cep && (
                          <p>{contactData.address.cep}</p>
                        )}

                        {contactData.address.city && (
                          <p>{contactData.address.city}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* E-mail */}
                {contactData?.email && (
                  <div className="flex items-start gap-3">
                    <div
                      className="
                        w-9 h-9
                        rounded-lg
                        bg-green-600/10
                        flex items-center justify-center
                        shrink-0
                      "
                    >
                      <Mail className="w-[17px] h-[17px] text-green-400" />
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                        E-mail
                      </p>

                      <a
                        href={`mailto:${contactData.email}`}
                        className="
                          text-sm
                          text-gray-300
                          hover:text-green-400
                          transition-colors
                          break-all
                        "
                      >
                        {contactData.email}
                      </a>
                    </div>
                  </div>
                )}

                {/* Telefone */}
                {contactData?.phone && (
                  <div className="flex items-start gap-3">
                    <div
                      className="
                        w-9 h-9
                        rounded-lg
                        bg-green-600/10
                        flex items-center justify-center
                        shrink-0
                      "
                    >
                      <Phone className="w-[17px] h-[17px] text-green-400" />
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                        Telefone
                      </p>

                      <a
                        href={`tel:${contactData.phone.replace(/\s/g, "")}`}
                        className="
                          text-sm
                          text-gray-300
                          hover:text-green-400
                          transition-colors
                        "
                      >
                        {contactData.phone}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400 leading-relaxed">
                Os canais institucionais de contato serão disponibilizados
                neste espaço.
              </p>
            )}
          </div>
        </div>

        {/* =====================================================
            MISSÃO
        ===================================================== */}
        <div className="mt-14 pt-10 border-t border-white/10">
          <div
            className="
              relative
              overflow-hidden
              border border-green-500/15
              bg-gradient-to-r
              from-green-500/10
              via-emerald-500/5
              to-transparent
              px-6 py-7
              sm:px-8
              rounded-2xl
            "
          >
            <div className="absolute top-0 left-0 h-full w-1 bg-green-500 rounded-l-2xl" />

            <p className="text-xs uppercase tracking-[0.2em] text-green-400 font-semibold mb-3">
              Nossa Missão
            </p>

            <p className="text-gray-200 text-base sm:text-lg leading-relaxed max-w-4xl">
              Impulsionar o desenvolvimento sustentável da Amazônia conectando
              pessoas, conhecimento, inovação e oportunidades.
            </p>
          </div>
        </div>

        {/* =====================================================
            BARRA INFERIOR
        ===================================================== */}
        <div
          className="
            mt-10
            pt-8
            border-t border-white/10
            flex flex-col
            lg:flex-row
            justify-between
            items-start lg:items-center
            gap-6
          "
        >
          <p className="text-sm text-gray-500">
            © {currentYear} Instituto Mercosul Amazônia – IMA. Todos os direitos
            reservados.
          </p>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-gray-500">
            <button
              type="button"
              className="hover:text-green-400 transition-colors"
            >
              Política de Privacidade
            </button>

            <button
              type="button"
              className="hover:text-green-400 transition-colors"
            >
              Termos de Uso
            </button>

            <button
              type="button"
              className="hover:text-green-400 transition-colors"
            >
              Código de Conduta
            </button>
          </div>

          <button
            type="button"
            onClick={() => scrollToSection("hero")}
            className="
              lg:ml-2
              inline-flex
              items-center
              justify-center
              w-10 h-10
              rounded-full
              border border-white/10
              bg-white/5
              hover:bg-green-600
              hover:border-green-600
              text-gray-400
              hover:text-white
              transition-all
            "
            aria-label="Voltar ao topo"
            title="Voltar ao topo"
          >
            <ArrowUp className="w-4 h-4" />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;